import { useEffect, useMemo, useState } from "react";
import client from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Printer, MagnifyingGlass, FunnelSimple, XCircle } from "@phosphor-icons/react";

const CATEGORY_OPTIONS = [
  { key: "saleable_with_box",                    label: "Saleable — With Box" },
  { key: "saleable_without_box",                 label: "Saleable — Without Box" },
  { key: "non_moving",                           label: "Non Moving" },
  { key: "defective",                            label: "Defective" },
  { key: "saleable_without_box_pb_tag_sticker",  label: "Saleable Without Box · PB Tag Sticker" },
  { key: "damaged",                              label: "Damaged" },
];

/** Convert yyyy-mm-dd or dd-mm-yyyy or ISO datetime to dd-mm-yyyy. Empty → "". */
function toDMY(v) {
  if (!v) return "";
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()}`;
  }
  return s;
}

export default function PrintGRSRLabel() {
  const [grsrList, setGrsrList] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [grsrId, setGrsrId] = useState("");
  const [grsrSearch, setGrsrSearch] = useState("");
  const [grsrSearchOpen, setGrsrSearchOpen] = useState(false);
  const [categoryKey, setCategoryKey] = useState("");

  // Filters
  const [fGrsrNo, setFGrsrNo] = useState("");
  const [fRetName, setFRetName] = useState("");
  const [fRetCode, setFRetCode] = useState("");
  const [fSalesman, setFSalesman] = useState("all");

  useEffect(() => {
    (async () => {
      const [{ data: g }, { data: s }, { data: r }] = await Promise.all([
        client.get("/grsr"), client.get("/salesmen"), client.get("/retailers"),
      ]);
      // Show only pending GRSR — those without a Shopper CN No entry.
      const pending = g.filter(x => !((x.shopper_cn_no || "").toString().trim()));
      setGrsrList(pending); setSalesmen(s); setRetailers(r);
    })();
  }, []);

  const retailerById = useMemo(() => {
    const m = new Map();
    retailers.forEach(r => m.set(r.id, r));
    return m;
  }, [retailers]);

  const filtered = useMemo(() => {
    const gNo = fGrsrNo.trim().toLowerCase();
    const rN  = fRetName.trim().toLowerCase();
    const rC  = fRetCode.trim().toLowerCase();
    return grsrList.filter(g => {
      if (gNo && !(g.grsr_no || "").toLowerCase().includes(gNo)) return false;
      if (rN && !(g.retailer_name || "").toLowerCase().includes(rN)) return false;
      if (rC) {
        const ret = retailerById.get(g.retailer_id);
        const code = (ret?.retailer_code || "").toLowerCase();
        if (!code.includes(rC)) return false;
      }
      if (fSalesman && fSalesman !== "all" && g.salesman_name !== fSalesman) return false;
      return true;
    });
  }, [grsrList, fGrsrNo, fRetName, fRetCode, fSalesman, retailerById]);

  const clearFilters = () => {
    setFGrsrNo(""); setFRetName(""); setFRetCode(""); setFSalesman("all");
  };

  const grsr = useMemo(() => grsrList.find(g => g.id === grsrId), [grsrList, grsrId]);
  const categoryLabel = CATEGORY_OPTIONS.find(c => c.key === categoryKey)?.label || "";
  const categoryValue = grsr && categoryKey ? (grsr[categoryKey] || 0) : 0;
  const actualQty = grsr?.actual_quantity_received || 0;
  const canPrint = grsr && categoryKey;

  return (
    <div>
      <PageHeader title="Print GRSR Label" subtitle="A4 label · Search GRSR, pick category, print" />
      <Section>
        {/* Filters */}
        <div data-testid="grsrlabel-filter-bar" className="screen-only bg-white border border-slate-200 rounded-sm p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <FunnelSimple size={16} className="text-slate-600" weight="duotone"/>
            <div className="label-cap text-slate-700">Search GRSR</div>
            {(fGrsrNo || fRetName || fRetCode || fSalesman !== "all") && (
              <button
                data-testid="grsrlabel-filter-clear"
                onClick={clearFilters}
                className="ml-auto text-xs font-semibold text-rose-600 hover:underline flex items-center gap-1"
              >
                <XCircle size={13}/> Clear all
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="label-cap">GRSR No</label>
              <Input data-testid="grsrlabel-filter-grsr-no" value={fGrsrNo} onChange={(e)=>setFGrsrNo(e.target.value)} placeholder="Contains…"/>
            </div>
            <div>
              <label className="label-cap">Retailer Name</label>
              <Input data-testid="grsrlabel-filter-retailer-name" value={fRetName} onChange={(e)=>setFRetName(e.target.value)} placeholder="Contains…"/>
            </div>
            <div>
              <label className="label-cap">Retailer Code</label>
              <Input data-testid="grsrlabel-filter-retailer-code" value={fRetCode} onChange={(e)=>setFRetCode(e.target.value)} placeholder="Contains…"/>
            </div>
            <div>
              <label className="label-cap">Salesman</label>
              <Select value={fSalesman} onValueChange={setFSalesman}>
                <SelectTrigger data-testid="grsrlabel-filter-salesman" className="rounded-sm"><SelectValue placeholder="All"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {salesmen.map(s => <SelectItem key={s.mobile} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-500" data-testid="grsrlabel-filter-count">
            <MagnifyingGlass size={12} className="inline mr-1"/>
            <b>{filtered.length}</b> of {grsrList.length} pending GRSR entries match
          </div>
        </div>

        {/* Picker & category */}
        <div className="screen-only bg-white border border-slate-200 rounded-sm p-6 max-w-3xl">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="relative">
              <label className="label-cap">Select GRSR (search by no / retailer / salesman)</label>
              <div className="relative">
                <Input
                  data-testid="grsrlabel-grsr-search"
                  value={grsrSearch}
                  onChange={(e)=>{
                    setGrsrSearch(e.target.value);
                    setGrsrSearchOpen(true);
                    if (grsrId) setGrsrId("");
                  }}
                  onFocus={()=>setGrsrSearchOpen(true)}
                  placeholder={filtered.length ? "Type GRSR no or retailer…" : "No pending GRSR"}
                  disabled={filtered.length === 0}
                  className="pr-8 rounded-sm"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {grsrId ? (
                    <button
                      type="button" data-testid="grsrlabel-grsr-clear"
                      onClick={()=>{ setGrsrId(""); setGrsrSearch(""); setGrsrSearchOpen(false); }}
                      className="text-slate-400 hover:text-rose-600"
                    ><XCircle size={16}/></button>
                  ) : (
                    <MagnifyingGlass size={14} className="text-slate-400"/>
                  )}
                </div>
              </div>
              {grsrSearchOpen && !grsrId && (
                <>
                  <div className="fixed inset-0 z-40" aria-hidden onClick={()=>setGrsrSearchOpen(false)}/>
                  <div
                    data-testid="grsrlabel-grsr-suggestions"
                    className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-y-auto z-50"
                  >
                    {(() => {
                      const s = grsrSearch.trim().toLowerCase();
                      const list = (s
                        ? filtered.filter(g =>
                            (g.grsr_no || "").toLowerCase().includes(s) ||
                            (g.retailer_name || "").toLowerCase().includes(s) ||
                            (g.salesman_name || "").toLowerCase().includes(s))
                        : filtered).slice(0, 20);
                      if (list.length === 0) {
                        return <div className="px-3 py-2 text-xs text-slate-500">
                          {filtered.length === 0 ? "No pending GRSR" : `No matches for "${grsrSearch}"`}
                        </div>;
                      }
                      return list.map(g => (
                        <button
                          key={g.id}
                          type="button"
                          data-testid={`grsrlabel-grsr-suggestion-${g.id}`}
                          onClick={()=>{
                            setGrsrId(g.id);
                            setGrsrSearch(`${g.grsr_no} · ${g.retailer_name}`);
                            setGrsrSearchOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center justify-between gap-3 border-b border-slate-100 last:border-b-0"
                        >
                          <div className="min-w-0">
                            <div className="font-mono font-semibold truncate">{g.grsr_no}</div>
                            <div className="text-xs text-slate-500 truncate">{g.retailer_name}</div>
                          </div>
                          <div className="text-xs text-slate-700 shrink-0">{g.salesman_name}</div>
                        </button>
                      ));
                    })()}
                  </div>
                </>
              )}
            </div>
            <div>
              <label className="label-cap">Select Category</label>
              <Select value={categoryKey} onValueChange={setCategoryKey}>
                <SelectTrigger data-testid="grsrlabel-category-select" className="rounded-sm">
                  <SelectValue placeholder="Pick a category"/>
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(c => (
                    <SelectItem key={c.key} value={c.key}>
                      {c.label}{grsr ? ` · ${grsr[c.key] || 0}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {grsr && (
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm bg-slate-50 border border-slate-200 rounded-sm p-4">
              <div><div className="label-cap">GRSR No</div><div className="font-mono font-semibold">{grsr.grsr_no}</div></div>
              <div><div className="label-cap">GRSR Date</div><div className="font-mono">{toDMY(grsr.grsr_date) || "—"}</div></div>
              <div><div className="label-cap">Retailer</div><div className="font-semibold">{grsr.retailer_name}</div></div>
              <div><div className="label-cap">Salesman</div><div className="font-semibold">{grsr.salesman_name}</div></div>
              <div><div className="label-cap">Actual Qty Received</div><div className="font-mono">{actualQty}</div></div>
              <div><div className="label-cap">Selected Category Qty</div><div className="font-mono">{categoryKey ? categoryValue : "—"}</div></div>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <Button
              data-testid="grsrlabel-print-btn"
              onClick={() => window.print()}
              disabled={!canPrint}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm"
            >
              <Printer size={16} className="mr-1" weight="bold"/> Print A4 Label
            </Button>
            {!grsr && <span className="text-xs text-slate-500">Pick a GRSR first</span>}
            {grsr && !categoryKey && <span className="text-xs text-rose-600">Pick a category to enable print</span>}
          </div>
        </div>

        {/* On-screen preview */}
        {canPrint && (
          <div className="screen-only mt-8">
            <div className="label-cap mb-2">Preview</div>
            <div className="grsrlabel-preview shadow-md">
              <GrsrLabelBody
                grsrNo={grsr.grsr_no}
                grsrDate={toDMY(grsr.grsr_date)}
                retailerName={grsr.retailer_name}
                salesmanName={grsr.salesman_name}
                actualQty={actualQty}
                categoryLabel={categoryLabel}
                categoryValue={categoryValue}
                quickbillNo={grsr.quickbill_no}
                quickbillCnDate={toDMY(grsr.quickbill_cn_date)}
                shopperCnNo={grsr.shopper_cn_no}
                shopperCnDate={toDMY(grsr.shopper_cn_date)}
              />
            </div>
          </div>
        )}

        {/* Print area */}
        <div className="print-area" id="grsrlabel-print-area">
          {canPrint && (
            <div className="a4-page-grsr">
              <GrsrLabelBody
                grsrNo={grsr.grsr_no}
                grsrDate={toDMY(grsr.grsr_date)}
                retailerName={grsr.retailer_name}
                salesmanName={grsr.salesman_name}
                actualQty={actualQty}
                categoryLabel={categoryLabel}
                categoryValue={categoryValue}
                quickbillNo={grsr.quickbill_no}
                quickbillCnDate={toDMY(grsr.quickbill_cn_date)}
                shopperCnNo={grsr.shopper_cn_no}
                shopperCnDate={toDMY(grsr.shopper_cn_date)}
              />
            </div>
          )}
        </div>

        <style>{`
          .grsrlabel-preview { width: 210mm; height: 297mm; background: white; border: 1px solid #d4d4d8; max-width: 100%; transform-origin: top left; }
          .a4-page-grsr { width: 210mm; height: 297mm; background: white; box-sizing: border-box; overflow: hidden; }
          @media screen {
            .print-area { display: none; }
            @media (max-width: 900px) { .grsrlabel-preview { transform: scale(0.45); } }
          }
          @media print {
            @page { size: 210mm 297mm; margin: 0; }
            html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
            body * { visibility: hidden !important; }
            #grsrlabel-print-area, #grsrlabel-print-area * { visibility: visible !important; }
            #grsrlabel-print-area { position: absolute; left: 0; top: 0; width: 210mm; display: block !important; }
          }
        `}</style>
      </Section>
    </div>
  );
}

function GrsrLabelBody({
  grsrNo, grsrDate, retailerName, salesmanName,
  actualQty, categoryLabel, categoryValue,
  quickbillNo, quickbillCnDate, shopperCnNo, shopperCnDate,
}) {
  const black = { color: "#000" };
  const val = (v) => (v == null || v === "") ? "—" : v;
  const CellLabel = { fontSize: "4mm", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", ...black };
  const CellValue = { fontSize: "8mm", fontWeight: 900, marginTop: "1mm", lineHeight: 1.05, fontFamily: "'IBM Plex Mono', monospace", ...black };
  return (
    <div style={{
      width: "100%", height: "100%",
      padding: "20mm",
      display: "flex", flexDirection: "column",
      border: "3px solid #000",
      boxSizing: "border-box",
      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      ...black,
    }}>
      {/* GRSR No + Date — top, biggest */}
      <div style={{ textAlign: "center", borderBottom: "1mm solid #000", paddingBottom: "6mm" }}>
        <div style={{ fontSize: "7mm", fontWeight: 700, letterSpacing: "0.4em", textTransform: "uppercase", ...black }}>
          GRSR
        </div>
        <div style={{ fontSize: "24mm", fontWeight: 900, lineHeight: 1.05, marginTop: "3mm", letterSpacing: "-0.02em", ...black }}>
          {grsrNo}
        </div>
        {grsrDate && (
          <div style={{ fontSize: "7mm", fontWeight: 700, marginTop: "3mm", letterSpacing: "0.02em", ...black }}>
            Date: <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{grsrDate}</span>
          </div>
        )}
      </div>

      {/* Retailer Name */}
      <div style={{ textAlign: "center", marginTop: "8mm" }}>
        <div style={{ fontSize: "6mm", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", ...black }}>
          Retailer
        </div>
        <div style={{ fontSize: "16mm", fontWeight: 900, lineHeight: 1.05, marginTop: "2mm", ...black }}>
          {retailerName}
        </div>
      </div>

      {/* Salesman */}
      <div style={{ textAlign: "center", marginTop: "7mm" }}>
        <div style={{ fontSize: "6mm", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", ...black }}>
          Salesman
        </div>
        <div style={{ fontSize: "12mm", fontWeight: 900, lineHeight: 1.05, marginTop: "2mm", ...black }}>
          {salesmanName}
        </div>
      </div>

      {/* Quickbill + Shopper CN block */}
      <div style={{
        marginTop: "7mm",
        border: "0.4mm solid #000",
        padding: "5mm 6mm",
        display: "grid", gridTemplateColumns: "1fr 1fr",
        rowGap: "4mm", columnGap: "6mm",
        ...black,
      }}>
        <div>
          <div style={CellLabel}>Quickbill No</div>
          <div style={CellValue}>{val(quickbillNo)}</div>
        </div>
        <div>
          <div style={CellLabel}>Quickbill CN Date</div>
          <div style={CellValue}>{val(quickbillCnDate)}</div>
        </div>
        <div>
          <div style={CellLabel}>Shopper CN No</div>
          <div style={CellValue}>{val(shopperCnNo)}</div>
        </div>
        <div>
          <div style={CellLabel}>Shopper CN Date</div>
          <div style={CellValue}>{val(shopperCnDate)}</div>
        </div>
      </div>

      {/* Bottom quantities row */}
      <div style={{
        marginTop: "auto", paddingTop: "8mm",
        borderTop: "1mm solid #000",
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5mm",
        ...black,
      }}>
        <div style={{ textAlign: "center", borderRight: "0.6mm solid #000", paddingRight: "5mm" }}>
          <div style={{ fontSize: "5mm", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", ...black }}>
            Actual Qty
          </div>
          <div style={{ fontSize: "24mm", fontWeight: 900, lineHeight: 1, marginTop: "4mm", letterSpacing: "-0.05em", ...black }}>
            {actualQty}
          </div>
        </div>
        <div style={{ textAlign: "center", paddingLeft: "5mm" }}>
          <div style={{ fontSize: "4mm", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", lineHeight: 1.2, minHeight: "10mm", ...black }}>
            {categoryLabel}
          </div>
          <div style={{ fontSize: "24mm", fontWeight: 900, lineHeight: 1, marginTop: "4mm", letterSpacing: "-0.05em", ...black }}>
            {categoryValue}
          </div>
        </div>
      </div>
    </div>
  );
}
